import type { LucideIcon } from 'lucide-react';
import {
  AlertCircle,
  AlertTriangle,
  ArrowDown,
  ArrowUp,
  Ban,
  Bell,
  Bookmark,
  Briefcase,
  Building2,
  Calendar,
  CalendarClock,
  Check,
  CheckCircle,
  ClipboardList,
  Clock,
  CreditCard,
  Droplets,
  Eye,
  Flag,
  Flame,
  Forward,
  Hash,
  HelpCircle,
  Home,
  Info,
  Leaf,
  ListTodo,
  Mail,
  MapPin,
  Megaphone,
  MessageSquare,
  Minus,
  Package,
  Phone,
  PhoneForwarded,
  PhoneOff,
  Receipt,
  Settings,
  Shield,
  Sparkles,
  Star,
  Tag,
  ThumbsDown,
  ThumbsUp,
  User,
  UserCheck,
  UserPlus,
  Users,
  UserX,
  Wrench,
  XCircle,
  Zap,
} from 'lucide-react';

export type TagIconOption = {
  name: string;
  Icon: LucideIcon;
  label: string;
};

/**
 * Icons for call-analysis tags: caller_type, intent, priority, action_required,
 * plus common “label this bucket” shapes. Labels describe the primary fit.
 */
export const TAG_ICON_OPTIONS: TagIconOption[] = [
  /* ── caller_type: who is calling ── */
  { name: 'UserPlus', Icon: UserPlus, label: 'New lead / first-time caller' },
  { name: 'UserCheck', Icon: UserCheck, label: 'Existing customer' },
  { name: 'UserX', Icon: UserX, label: 'Unrecognized / wrong contact' },
  { name: 'Briefcase', Icon: Briefcase, label: 'Vendor / salesperson' },
  { name: 'Megaphone', Icon: Megaphone, label: 'Spam / unsolicited pitch' },
  { name: 'Ban', Icon: Ban, label: 'Blocked / unwanted caller' },
  { name: 'PhoneOff', Icon: PhoneOff, label: 'Wrong number / misdial' },
  { name: 'Building2', Icon: Building2, label: 'Internal / staff / testing' },
  { name: 'Settings', Icon: Settings, label: 'Configuration / setup call' },

  /* ── intent: why they called ── */
  { name: 'Sparkles', Icon: Sparkles, label: 'New service / sales inquiry' },
  { name: 'Wrench', Icon: Wrench, label: 'Issue / problem report' },
  { name: 'Calendar', Icon: Calendar, label: 'Scheduling / appointments' },
  { name: 'CalendarClock', Icon: CalendarClock, label: 'Time-specific booking' },
  { name: 'CreditCard', Icon: CreditCard, label: 'Billing / payment' },
  { name: 'Receipt', Icon: Receipt, label: 'Invoice / charges' },
  { name: 'Info', Icon: Info, label: 'General information' },
  { name: 'HelpCircle', Icon: HelpCircle, label: 'Questions / how-it-works' },
  { name: 'MessageSquare', Icon: MessageSquare, label: 'Message for someone' },
  { name: 'Forward', Icon: Forward, label: 'Relay / transfer / pass along' },
  { name: 'User', Icon: User, label: 'Reach a specific person' },
  { name: 'XCircle', Icon: XCircle, label: 'Cancellation / opt-out' },

  /* ── priority ── */
  { name: 'Flame', Icon: Flame, label: 'Critical / emergency' },
  { name: 'Zap', Icon: Zap, label: 'Urgent / needs fast response' },
  { name: 'AlertTriangle', Icon: AlertTriangle, label: 'High priority' },
  { name: 'ArrowUp', Icon: ArrowUp, label: 'Elevated / above normal' },
  { name: 'Minus', Icon: Minus, label: 'Normal / routine' },
  { name: 'ArrowDown', Icon: ArrowDown, label: 'Low priority' },
  { name: 'Phone', Icon: Phone, label: 'Missed / no live conversation' },

  /* ── action_required & outcomes ── */
  { name: 'ListTodo', Icon: ListTodo, label: 'Follow-up task / callback list' },
  { name: 'PhoneForwarded', Icon: PhoneForwarded, label: 'Callback promised' },
  { name: 'Bell', Icon: Bell, label: 'Needs team attention' },
  { name: 'CheckCircle', Icon: CheckCircle, label: 'Handled / resolved on call' },
  { name: 'Check', Icon: Check, label: 'No further action' },

  /* ── general tagging ── */
  { name: 'Tag', Icon: Tag, label: 'Generic tag' },
  { name: 'Hash', Icon: Hash, label: 'Category / code' },
  { name: 'Bookmark', Icon: Bookmark, label: 'Saved / notable' },
  { name: 'Flag', Icon: Flag, label: 'Flagged item' },
  { name: 'MapPin', Icon: MapPin, label: 'Location / territory' },
  { name: 'Star', Icon: Star, label: 'VIP / highlight' },
  { name: 'Package', Icon: Package, label: 'Order / delivery / job' },
  { name: 'Home', Icon: Home, label: 'Residential / property' },
  { name: 'Users', Icon: Users, label: 'Group / household / team' },
  { name: 'Mail', Icon: Mail, label: 'Email channel' },
  { name: 'Clock', Icon: Clock, label: 'Time / SLA' },
  { name: 'ClipboardList', Icon: ClipboardList, label: 'Checklist / details' },
];

/** Icons removed from the picker but still resolved for existing saved definitions. */
const LEGACY_ICON_ENTRIES: [string, LucideIcon][] = [
  ['AlertCircle', AlertCircle],
  ['Shield', Shield],
  ['ThumbsUp', ThumbsUp],
  ['ThumbsDown', ThumbsDown],
  ['Leaf', Leaf],
  ['Droplets', Droplets],
  ['Eye', Eye],
];

const ICON_BY_EXACT_NAME = new Map<string, LucideIcon>([
  ...TAG_ICON_OPTIONS.map(o => [o.name, o.Icon] as const),
  ...LEGACY_ICON_ENTRIES,
]);

/**
 * Resolves a stored icon string to a Lucide component. Matches exact export name
 * or case-insensitive name (e.g. legacy "phone" → Phone).
 */
export function resolveTagIcon(name: string): LucideIcon | null {
  const trimmed = name.trim();
  if (!trimmed) return null;
  const exact = ICON_BY_EXACT_NAME.get(trimmed);
  if (exact) return exact;
  const lower = trimmed.toLowerCase();
  for (const opt of TAG_ICON_OPTIONS) {
    if (opt.name.toLowerCase() === lower) {
      return opt.Icon;
    }
  }
  for (const [key, Icon] of LEGACY_ICON_ENTRIES) {
    if (key.toLowerCase() === lower) {
      return Icon;
    }
  }
  return null;
}
